import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { ShieldCheck } from 'lucide-react';
import { BRAND } from '@shripos/shared/brand';
import { adminLoginSchema, forgotPasswordSchema, password as passwordRule } from '@shripos/shared/schemas';
import { z } from 'zod';
import {
  Alert,
  Button,
  Card,
  CopyButton,
  Field,
  Input,
  Spinner,
  applyServerErrors,
  errorAt,
  takeTokenFromHash,
} from '@shripos/ui';
import { api } from '../../lib/api.js';
import { useSession } from '../../stores/session.js';

export function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <img src="/favicon.svg" alt="" className="size-10" />
          <span className="text-xl font-semibold text-slate-900">{BRAND.adminAppName}</span>
        </div>
        <Card className="p-6 sm:p-8">
          <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </Card>
        {footer && <div className="mt-4 text-center text-sm text-slate-500">{footer}</div>}
      </div>
    </div>
  );
}

const REASONS = {
  idle: 'You were signed out after 30 minutes of inactivity.',
  expired: 'Your session has ended. Please sign in again.',
};

export function LoginPage() {
  const navigate = useNavigate();
  const reason = useSession((s) => s.signedOutReason);
  const [error, setError] = useState(null);
  const { register, handleSubmit, formState } = useForm({ resolver: zodResolver(adminLoginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      const res = await api.post('/auth/login', values);
      useSession.getState().startMfa({ token: res.mfaToken, stage: res.mfaRequired, email: values.email });
      navigate('/login/mfa');
    } catch (err) {
      setError(err.message);
    }
  });

  return (
    <AuthShell title="Sign in" subtitle="Two-factor authentication is required for every admin account.">
      {reason && REASONS[reason] && (
        <Alert tone="info" className="mb-4">
          {REASONS[reason]}
        </Alert>
      )}
      {error && (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      )}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Email" error={errorAt(formState.errors, 'email')} htmlFor="email">
          <Input id="email" type="email" autoComplete="username" autoFocus {...register('email')} />
        </Field>
        <Field label="Password" error={errorAt(formState.errors, 'password')} htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" {...register('password')} />
        </Field>
        <Button type="submit" className="w-full" loading={formState.isSubmitting}>
          Continue
        </Button>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to="/forgot-password" className="text-brand-700 hover:underline">
          Forgot password?
        </Link>
      </p>
    </AuthShell>
  );
}

function CodeInput({ value, onChange, autoFocus }) {
  return (
    <Input
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      size="lg"
      autoFocus={autoFocus}
      className="text-center font-mono text-2xl tracking-[0.5em]"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      aria-label="6-digit code"
    />
  );
}

export function MfaPage() {
  const mfa = useSession((s) => s.mfa);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const pendingAdmin = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (mfa?.stage !== 'enroll' || started.current) return;
    started.current = true;
    api
      .post('/auth/mfa/enroll/start', { mfaToken: mfa.token })
      .then(setSetup)
      .catch((e) => setError(e.message));
  }, [mfa]);

  if (!mfa && !recoveryCodes) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mfa.stage === 'enroll') {
        const res = await api.post('/auth/mfa/enroll/confirm', { mfaToken: mfa.token, code });
        pendingAdmin.current = res.admin;
        setRecoveryCodes(res.recoveryCodes);
      } else {
        const body = useRecovery ? { mfaToken: mfa.token, recoveryCode: recovery } : { mfaToken: mfa.token, code };
        const res = await api.post('/auth/mfa/verify', body);
        useSession.getState().signedIn(res.admin);
      }
    } catch (err) {
      setError(err.code === 'MFA_INVALID' && err.message.includes('start') ? `${err.message}` : err.message);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (recoveryCodes) {
    return (
      <AuthShell
        title="Save your recovery codes"
        subtitle="Each code works once if you lose access to your authenticator app."
      >
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-4 font-mono text-sm ring-1 ring-slate-200">
          {recoveryCodes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div className="mt-2 flex justify-end">
          <CopyButton value={recoveryCodes.join('\n')} label="Copy all" />
        </div>
        <Alert tone="warning" className="mt-4">
          These codes will not be shown again. Store them somewhere safe.
        </Alert>
        <Button className="mt-6 w-full" onClick={() => useSession.getState().signedIn(pendingAdmin.current)}>
          I've saved them — continue
        </Button>
      </AuthShell>
    );
  }

  const enroll = mfa.stage === 'enroll';
  return (
    <AuthShell
      title={enroll ? 'Set up two-factor authentication' : 'Two-factor authentication'}
      subtitle={
        enroll
          ? 'Scan the QR code with Google Authenticator, Microsoft Authenticator, 1Password or similar, then enter the 6-digit code.'
          : `Enter the code from your authenticator app for ${mfa.email}.`
      }
      footer={
        <Link to="/login" onClick={() => useSession.getState().signedOut()} className="hover:underline">
          Start over
        </Link>
      }
    >
      {error && (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      )}
      {enroll && (
        <div className="mb-5 flex flex-col items-center gap-3">
          {setup ? (
            <>
              <img
                src={setup.qrDataUrl}
                alt="Authenticator QR code"
                className="size-52 rounded-lg ring-1 ring-slate-200"
              />
              <div className="text-center text-xs text-slate-500">
                Can't scan? Enter this key manually:
                <div className="mt-1 flex items-center justify-center gap-1">
                  <code className="rounded bg-slate-100 px-2 py-1 font-mono text-slate-800">{setup.secret}</code>
                  <CopyButton value={setup.secret} />
                </div>
              </div>
            </>
          ) : (
            <Spinner />
          )}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        {useRecovery ? (
          <Field label="Recovery code">
            <Input
              autoFocus
              value={recovery}
              onChange={(e) => setRecovery(e.target.value)}
              placeholder="XXXX-XXXX"
              className="font-mono"
            />
          </Field>
        ) : (
          <CodeInput value={code} onChange={setCode} autoFocus />
        )}
        <Button
          type="submit"
          className="w-full"
          icon={ShieldCheck}
          loading={busy}
          disabled={useRecovery ? recovery.length < 8 : code.length !== 6}
        >
          {enroll ? 'Turn on two-factor & sign in' : 'Verify'}
        </Button>
      </form>
      {!enroll && (
        <button
          type="button"
          onClick={() => {
            setUseRecovery((v) => !v);
            setError(null);
          }}
          className="mt-4 w-full text-center text-sm text-brand-700 hover:underline"
        >
          {useRecovery ? 'Use authenticator code instead' : 'Lost your phone? Use a recovery code'}
        </button>
      )}
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [done, setDone] = useState(null);
  const { register, handleSubmit, formState } = useForm({ resolver: zodResolver(forgotPasswordSchema) });
  const onSubmit = handleSubmit(async (values) => {
    const res = await api.post('/auth/forgot-password', values).catch((e) => ({ message: e.message }));
    setDone(res.message);
  });
  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a link that works once and expires in 30 minutes.">
      {done ? (
        <Alert tone="success">{done}</Alert>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Field label="Email" error={errorAt(formState.errors, 'email')}>
            <Input type="email" autoFocus autoComplete="username" {...register('email')} />
          </Field>
          <Button type="submit" className="w-full" loading={formState.isSubmitting}>
            Send reset link
          </Button>
        </form>
      )}
      <p className="mt-4 text-center text-sm">
        <Link to="/login" className="text-brand-700 hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}

const newPasswordForm = z
  .object({ password: passwordRule, confirm: z.string() })
  .refine((v) => v.password === v.confirm, { message: 'Passwords do not match', path: ['confirm'] });

/** Handles both /reset-password and /set-password (token arrives in the URL fragment). */
export function PasswordLinkPage({ mode }) {
  const location = useLocation();
  const [token] = useState(() => takeTokenFromHash());
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const {
    register,
    handleSubmit,
    formState,
    setError: setFieldError,
  } = useForm({ resolver: zodResolver(newPasswordForm) });

  const onSubmit = handleSubmit(async ({ password }) => {
    setError(null);
    try {
      await api.post(mode === 'set' ? '/auth/set-password' : '/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      applyServerErrors(setFieldError, err);
      setError(err.message);
    }
  });

  const title = mode === 'set' ? 'Choose your password' : 'Choose a new password';
  return (
    <AuthShell title={title} subtitle="At least 10 characters. A short phrase is easier to remember and hard to guess.">
      {!token && !done ? (
        <Alert tone="danger" title="Link incomplete">
          Open the link from your email again
          {location.pathname === '/set-password' ? ', or ask a super admin to resend the invite' : ''}.
        </Alert>
      ) : done ? (
        <>
          <Alert tone="success" title="Password saved">
            You can now sign in. {mode === 'set' && 'You will set up two-factor authentication next.'}
          </Alert>
          <Link to="/login">
            <Button className="mt-4 w-full">Go to sign in</Button>
          </Link>
        </>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label="New password" error={errorAt(formState.errors, 'password')}>
            <Input type="password" autoComplete="new-password" autoFocus {...register('password')} />
          </Field>
          <Field label="Confirm password" error={errorAt(formState.errors, 'confirm')}>
            <Input type="password" autoComplete="new-password" {...register('confirm')} />
          </Field>
          <Button type="submit" className="w-full" loading={formState.isSubmitting}>
            Save password
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
